/* Copyright (C) Okahu Inc 2023-2024. All rights reserved. */
'use client';

import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { useLanguage } from '@/providers/LanguageProvider';
import { Input } from '@/ux-common/src/components/ui/input';
import componentsArr from 'data/components-library.json';
import { ChevronDown } from 'lucide-react';
import { AssociableApps, Component, ComponentsData } from 'types/api-data';
import { Service } from 'types/services';

import { removeFilterDuplicates } from '@/lib/helper';
import { cn } from '@/lib/utils';
import { useComponents } from '@/hooks/useComponents';
import { useExperienceScopes } from '@/hooks/useExperienceScope';
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Separator } from '@/components/ui/separator';
import { EnhancedMultiSelect } from '@/components/combobox';
import { CustomBadge, CustomButton } from '@/components/common';
import { MetricDetail } from '@/components/common/metric-detail';
import { Icons } from '@/components/icons';
import { NotFoundMessage } from '@/components/not-found';
import { ITransformedArr } from '@/components/services-page/service-detail-modal/service-detail-modal';
import { DetailsPageSpinner } from '@/components/spinner/details-page-spinner';

import { ServiceDropdown } from '../service-dropdown';

export const GeneralTab = ({
  component,
  form,
  selectedApp,
  setSelectedApp,
  selectedWorkflows,
  setSelectedWorkflows,
  selectedService,
  setSelectedService,
  dependencyLoading,
  isArchived,
  experienceScopes,
}: {
  component: Component;
  form: any;
  selectedApp: AssociableApps[] | [];
  setSelectedApp: Dispatch<SetStateAction<AssociableApps[]>>;
  selectedWorkflows: ComponentsData;
  setSelectedWorkflows: Dispatch<SetStateAction<ComponentsData>>;
  selectedService: Service | null;
  setSelectedService: Dispatch<SetStateAction<Service | null>>;
  dependencyLoading: boolean;
  isArchived: boolean;
  experienceScopes: ReturnType<typeof useExperienceScopes>;
}) => {
  const { messages } = useLanguage();
  const [transformedArray, setTransformedArray] = useState<ITransformedArr>([]);

  const generalIntl = messages?.General;
  const {
    state: { associableApps, activeWorkflows },
  } = useComponents();

  // Handle custom scope fields
  const handleCustomScopeChange = (
    index: number,
    field: 'name' | 'value',
    newValue: string
  ) => {
    experienceScopes.setCustomScopes((prev) => {
      const updated = [...prev]; // Create a copy of previous scopes
      updated[index] = { ...updated[index], [field]: newValue }; // Update specific field
      return updated;
    });
  };

  // Add new empty custom scope
  const addCustomScope = () => {
    experienceScopes.setCustomScopes((prev) => [
      ...prev,
      { name: '', value: '' },
    ]);
  };

  // Remove custom scope by index
  const removeCustomScope = (index: number) => {
    experienceScopes.setCustomScopes(
      (prev) => prev.filter((_, i) => i !== index) // Filter out scope at given index
    );
  };

  // Initialize custom scopes when editing existing custom experience component
  useEffect(() => {
    if (
      component?.type === 'experience.custom' &&
      component?.properties?.scopes
    ) {
      const scopes = component.properties.scopes;
      const customScopesFromProps = Object.entries(scopes).map(
        ([name, value]) => ({
          name,
          value,
        })
      );
      // Set custom scopes
      experienceScopes.setCustomScopes(
        customScopesFromProps.length > 0
          ? customScopesFromProps
          : [{ name: '', value: '' }]
      );
    }
  }, [component?.component_name, component?.properties?.scopes]);

  useEffect(() => {
    const transformedArr = associableApps
      ? associableApps?.map((item: AssociableApps, index: number) => ({
          id: index + 1,
          label: item.display_name,
          value: item.name,
        }))
      : [];

    setTransformedArray(transformedArr);
  }, [associableApps]);

  const handleAddApplication = (apps: string[]) => {
    const newlySelectedApp = associableApps.filter((app: AssociableApps) =>
      apps.includes(app.name)
    );

    setSelectedApp(newlySelectedApp);
  };

  const handleDelete = (appName: string) => {
    setSelectedApp((prev) => prev.filter((app) => app.name !== appName));
  };

  // Workflow
  const type = component?.type;
  const isAgentOrTool = type && ['agent', 'tool'].includes(type.split('.')[0]);

  const handleAddWorkflow = (workflows: string[]) => {
    const newlySelectedWorkflow = activeWorkflows.filter((workflow: any) =>
      workflows.includes(workflow.component_name)
    );
    setSelectedWorkflows(newlySelectedWorkflow);
  };

  const workflowsTransformedArr = activeWorkflows
    ?.filter((comp: Component) => comp.type.startsWith('workflow.'))
    .map((item: Component, id: number) => ({
      id: id + 1,
      label: item.display_name,
      value: item.component_name,
    }));

  const handleDeleteWorkflow = (workflow: Component) => {
    const newArr = selectedWorkflows.filter(
      (selectedWorkflow) =>
        selectedWorkflow.component_name !== workflow.component_name
    );
    setSelectedWorkflows(newArr);
  };

  return (
    <div className="space-y-6">
      <MetricDetail lastUpdated={component.last_updated_at} />
      <FormField
        control={form.control}
        name="display_name"
        render={({ field }) => (
          <FormItem className="space-y-1">
            {!isArchived && (
              <>
                <FormLabel
                  htmlFor="name"
                  className="text-form-input-text font-normal text-form-input-rest"
                >
                  {generalIntl?.display_name} *
                </FormLabel>
                <FormControl>
                  <Input
                    id="name"
                    className="h-[42px] rounded-sm border border-form-control-border-rest placeholder:text-form-input-text placeholder:text-form-input-disabled"
                    placeholder={generalIntl?.instance_placeholder}
                    {...field}
                  />
                </FormControl>
              </>
            )}
            <FormMessage />
          </FormItem>
        )}
      />
      {component?.type.startsWith('experience.') && (
        <div className="space-y-1">
          <p className="text-form-input-text text-form-input-rest">
            {generalIntl?.experience_type}{' '}
          </p>
          {/* Experience Type Multi-Select Dropdown */}
          <EnhancedMultiSelect
            defaultValue={
              experienceScopes.selectedExperienceTypes?.map(
                (item) => item.id
              ) || []
            }
            showPills={false}
            asChild={true}
            options={componentsArr.components
              .filter(
                (item) =>
                  item.type.startsWith('experience.') &&
                  item.state === 'supported'
              )
              .map((item, index) => ({
                id: index + 1,
                label: item.component_display_name,
                value: item.type,
              }))}
            onValueChange={(selected) => {
              if (isArchived) return;

              const selectedOptions = componentsArr.components
                .filter((item) => item.type.startsWith('experience.'))
                .filter((item) => selected.includes(item.type))
                .map((item) => ({
                  id: item.type,
                  name: item.component_display_name,
                }));
              // Update selected experience types
              experienceScopes.setSelectedExperienceTypes?.(selectedOptions);

              // Clear all scopes if no types selected
              if (selected.length === 0) {
                experienceScopes.setAvailableScopes([]);
                experienceScopes.setPredefinedScopes({});
                experienceScopes.setSelectedScopes({});
                experienceScopes.setCustomScopes([]);
              }
              // Initialize empty custom scope if custom type selected
              if (
                selected.includes('experience.custom') &&
                experienceScopes.customScopes.length === 0
              ) {
                experienceScopes.setCustomScopes([{ name: '', value: '' }]);
              }

              if (selectedOptions.length === 1) {
                experienceScopes.setExperienceTypeSelected?.(
                  selectedOptions[0]
                );
              }
            }}
            placeholder={messages?.PromptFilters?.multi_select?.all}
            inputPlaceholder="Choose the experience type"
            hasDelete={!isArchived}
          >
            <div className="flex h-[42px] w-full cursor-pointer items-center justify-between rounded-sm border border-form-control-border-rest">
              <span className="ml-3 text-sm text-[#8C9EA1]">
                {experienceScopes.experienceTypeSelected
                  ? experienceScopes.experienceTypeSelected.name
                  : 'Choose experience type'}
              </span>
              <ChevronDown className="mx-2 size-4 cursor-pointer text-form-input-rest" />
            </div>
          </EnhancedMultiSelect>
        </div>
      )}

      {/* Facts Section */}
      {(experienceScopes.selectedExperienceTypes?.length || 0) > 0 && (
        <>
          {!isArchived && (
            <div className="mt-4 flex flex-col gap-1">
              <p className="flex items-center gap-1 text-form-input-text text-form-input-rest">
                {messages?.ComponentsPage.facts}{' '}
                {/* Show count badge for predefined scopes */}
                {!experienceScopes.selectedExperienceTypes?.every(
                  (et) => et.id === 'experience.custom'
                ) && (
                  <span className="flex h-6 w-fit items-center rounded-full bg-[#40565A] px-2 py-1 text-h5 leading-4 text-body-subtle">
                    {Object.keys(experienceScopes.selectedScopes || {})
                      .length || 0}
                  </span>
                )}
              </p>
              {/* Predefined Facts Multi-Select  */}
              {!experienceScopes.selectedExperienceTypes?.every(
                (et) => et.id === 'experience.custom'
              ) && (
                <EnhancedMultiSelect
                  defaultValue={Object.keys(
                    experienceScopes.selectedScopes || {}
                  )}
                  showPills={false}
                  loading={false}
                  options={(experienceScopes.availableScopes || []).map(
                    (scope, index) => ({
                      id: index + 1,
                      label: `${scope} (${(experienceScopes.predefinedScopes || {})[scope] || ''})`,
                      value: scope,
                    })
                  )}
                  onValueChange={(selected) => {
                    const newScopes = selected.reduce(
                      (acc, scope) => {
                        acc[scope] =
                          (experienceScopes.predefinedScopes || {})[scope] ||
                          '';
                        return acc;
                      },
                      {} as Record<string, string>
                    );
                    experienceScopes.setSelectedScopes(newScopes);
                  }}
                  placeholder={messages?.PromptFilters?.multi_select?.all}
                  inputPlaceholder="Choose the facts"
                  hasDelete={true}
                />
              )}
            </div>
          )}

          {/* Facts Display */}
          <div className="mt-1 border border-line-secondary">
            {experienceScopes.selectedExperienceTypes?.map((type) => (
              <div key={type.id} className="mb-4">
                {type.id === 'experience.custom' ? (
                  // Custom Experience Type
                  <div className="p-3">
                    <p className="mb-3 text-body text-form-input-disabled">
                      {messages?.ComponentsPage.custom_facts}{' '}
                    </p>
                    <div className="space-y-2">
                      {experienceScopes.customScopes.map((scope, index) => (
                        <div key={index} className="flex items-center gap-2">
                          <Input
                            value={scope.name}
                            onChange={(e) =>
                              handleCustomScopeChange(
                                index,
                                'name',
                                e.target.value
                              )
                            }
                            placeholder="Display Name"
                            disabled={isArchived}
                          />
                          <Input
                            value={scope.value}
                            onChange={(e) =>
                              handleCustomScopeChange(
                                index,
                                'value',
                                e.target.value
                              )
                            }
                            placeholder="Scope Variable Name"
                            disabled={isArchived}
                          />
                          {!isArchived && (
                            <CustomButton
                              type="button"
                              className="bg-transparent px-0 hover:bg-transparent focus:bg-[#257C9D1F] active:bg-[#257C9D29] disabled:bg-transparent"
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                removeCustomScope(index);
                              }}
                            >
                              {Icons.TrashIcon('size-4 stroke-1')}
                            </CustomButton>
                          )}
                        </div>
                      ))}
                      <CustomButton
                        type="button"
                        onClick={addCustomScope}
                        className="gap-1 bg-transparent pl-2 pr-3 hover:bg-transparent focus:bg-[#257C9D1F] active:bg-[#257C9D29] disabled:bg-transparent"
                      >
                        {Icons.Plus('text-ocean size-4')}
                        <span className="text-btn-label text-ocean">
                          {messages?.ComponentsPage.add_more}
                        </span>
                      </CustomButton>
                    </div>
                  </div>
                ) : (
                  // Predefined Experience Type
                  <>
                    {Object.keys(experienceScopes.selectedScopes || {})
                      .length === 0 ? (
                      <div className="mt-1 h-[200px]">
                        <NotFoundMessage
                          title="No facts associated"
                          message="Select one or more facts to associate"
                          outerDivClassName="gap-1 py-8 my-[0px]"
                        />
                      </div>
                    ) : (
                      <div className="mt-1 space-y-2">
                        {Object.entries(experienceScopes.selectedScopes || {})
                          .filter(
                            ([scopeName]) =>
                              !experienceScopes.customScopes.some(
                                (custom) => custom.name === scopeName
                              )
                          )
                          .map(([scopeName, propName]) => (
                            <div
                              key={scopeName}
                              className="flex items-center justify-between border-b border-b-line-secondary p-3"
                            >
                              <span className="text-body">
                                {scopeName}
                                {propName && (
                                  <span className="ml-2 text-form-input-text text-form-input-disabled">
                                    {propName}
                                  </span>
                                )}
                              </span>
                              {!isArchived && (
                                <div className="flex items-center">
                                  <CustomButton
                                    className="flex items-center gap-2 bg-transparent hover:bg-transparent focus:bg-[#257C9D1F] active:bg-[#257C9D29] disabled:bg-transparent"
                                    onClick={() =>
                                      experienceScopes.handleMakeCustom?.(
                                        scopeName,
                                        propName
                                      )
                                    }
                                  >
                                    {Icons.SpannerIcon(
                                      'size-4 text-[#257C9D] cursor-pointer'
                                    )}
                                    <span className="text-form-input-text font-medium text-ocean">
                                      {messages?.ComponentsPage.make_custom}
                                    </span>
                                  </CustomButton>
                                  <Separator
                                    orientation="vertical"
                                    className="mx-3 h-full min-h-6 bg-line-tertiary"
                                  />
                                  <CustomButton
                                    type="button"
                                    className="bg-transparent px-0 hover:bg-transparent focus:bg-[#257C9D1F] active:bg-[#257C9D29] disabled:bg-transparent"
                                    onClick={(e) => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      experienceScopes.handleDeleteScope?.(
                                        scopeName
                                      );
                                    }}
                                  >
                                    {Icons.TrashIcon(
                                      'size-4 stroke-1 cursor-pointer'
                                    )}
                                  </CustomButton>
                                </div>
                              )}
                            </div>
                          ))}
                      </div>
                    )}
                  </>
                )}
              </div>
            )) || []}
          </div>
        </>
      )}
      {!isAgentOrTool && !component?.type.startsWith('experience.') ? (
        <ServiceDropdown
          selectedService={selectedService}
          onFieldChange={setSelectedService}
          isDisabled={isArchived}
        />
      ) : null}
      <div className="space-y-[3px]">
        <div className="text-form-input-text text-form-input-rest">
          <span>
            {isAgentOrTool
              ? messages?.ComponentsPage.workflow_associated
              : messages?.ComponentsPage?.application_associated}
          </span>
          <CustomBadge
            customSize="lg"
            variant="secondary"
            className="ml-1 bg-[#40565A] text-body-subtle hover:bg-[#40565A]"
          >
            {isAgentOrTool ? selectedWorkflows.length : selectedApp.length}
          </CustomBadge>
        </div>
        {!isArchived &&
          (isAgentOrTool ? (
            <EnhancedMultiSelect
              defaultValue={selectedWorkflows?.map(
                (item) => item.component_name
              )}
              hasDelete={true}
              showPills={false}
              loading={dependencyLoading}
              options={removeFilterDuplicates(workflowsTransformedArr)}
              onValueChange={handleAddWorkflow}
              placeholder={messages?.PromptFilters?.multi_select?.all}
              inputPlaceholder={
                messages?.ComponentsPage?.choose_workflows_to_be_associated
              }
            />
          ) : (
            <EnhancedMultiSelect
              defaultValue={selectedApp?.map((item) => item.name ?? '')}
              hasDelete={true}
              showPills={false}
              loading={dependencyLoading}
              options={transformedArray}
              onValueChange={handleAddApplication}
              placeholder={messages?.PromptFilters?.multi_select?.all}
              inputPlaceholder={
                messages?.ComponentsPage?.choose_applications_to_be_associated
              }
            />
          ))}

        {isAgentOrTool ? (
          <div
            className={cn(
              'h-[200px] overflow-y-auto rounded-sm border ',
              selectedWorkflows?.length > 0
                ? 'border-form-control-border-rest'
                : 'border-[#40565A]'
            )}
          >
            {dependencyLoading ? (
              <div className="flex min-h-40 items-center justify-center">
                <DetailsPageSpinner />
              </div>
            ) : selectedWorkflows.length > 0 ? (
              <ul className="list-none">
                {selectedWorkflows.map((comp: Component) => (
                  <li
                    key={comp.component_name}
                    className={`flex justify-between border-b border-b-line-secondary p-3 text-sm`}
                  >
                    <span>{comp.display_name}</span>
                    {!isArchived && (
                      <span onClick={() => handleDeleteWorkflow(comp)}>
                        {Icons.TrashIcon('size-5 stroke-1 cursor-pointer')}
                      </span>
                    )}
                  </li>
                ))}
              </ul>
            ) : (
              <NotFoundMessage
                title={messages?.ComponentsPage?.no_workflow_associated ?? ''}
                message={
                  messages?.ComponentsPage
                    ?.select_one_or_more_workflow_and_associate
                }
                outerDivClassName="gap-1 py-8 my-[0px] h-full"
              />
            )}
          </div>
        ) : (
          <div
            className={cn(
              'h-[200px] overflow-y-auto rounded-sm border',
              selectedApp?.length > 0
                ? 'border-form-control-border-rest'
                : 'border-[#40565A]'
            )}
          >
            {dependencyLoading ? (
              <div className="flex min-h-40 items-center justify-center">
                <DetailsPageSpinner />
              </div>
            ) : selectedApp.length > 0 ? (
              <ul className="list-none">
                {selectedApp.map((app) => (
                  <li
                    key={app.name}
                    className={`flex justify-between border-b border-b-line-secondary p-3 text-sm`}
                  >
                    <span>{app.display_name}</span>
                    {!isArchived && (
                      <span onClick={() => handleDelete(app.name)}>
                        {Icons.TrashIcon('size-5 stroke-1 cursor-pointer')}
                      </span>
                    )}
                  </li>
                ))}
              </ul>
            ) : (
              <NotFoundMessage
                title={
                  messages?.ComponentsPage?.no_application_associated ?? ''
                }
                message={
                  messages?.ComponentsPage
                    ?.select_one_or_more_application_and_associate
                }
                outerDivClassName="gap-1 py-8 my-[0px] h-full"
              />
            )}
          </div>
        )}
      </div>
    </div>
  );
};
